import { useState, useMemo, type FormEvent } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') ?? '', [params]);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) {
      setError('Link inválido. Solicite um novo e-mail de redefinição.');
      return;
    }
    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao redefinir a senha';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-bg-base">
      <div className="hidden lg:flex w-80 shrink-0 flex-col justify-between border-r border-border px-10 py-12">
        <div>
          <span className="font-serif text-2xl text-text-primary leading-none">Nobel</span>
          <br />
          <span className="font-serif text-2xl text-text-primary leading-none">Capital</span>
        </div>
        <div className="flex flex-col gap-3 opacity-30">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="h-px bg-border" />
          ))}
        </div>
        <p className="text-2xs text-text-muted tracking-[0.15em] uppercase">
          Recrutamento &amp; Expansão
        </p>
      </div>

      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-sm animate-slide-up">

          <div className="mb-10 lg:hidden">
            <p className="font-serif text-2xl text-text-primary">Nobel Capital</p>
            <p className="text-2xs text-text-muted tracking-widest uppercase mt-1">Expansion CRM</p>
          </div>

          <div className="mb-8">
            <h1 className="font-serif text-2xl text-text-primary">Nova senha</h1>
            <p className="mt-1 text-sm text-text-secondary">Digite sua nova senha abaixo.</p>
          </div>

          {done ? (
            <p className="rounded-sm border border-status-green/25 bg-status-green/5 px-3 py-2 text-xs text-status-green">
              Senha alterada com sucesso. Redirecionando…
            </p>
          ) : !token ? (
            <p className="rounded-sm border border-status-red/25 bg-status-red/5 px-3 py-2 text-xs text-status-red">
              Link inválido. Solicite um novo e-mail de redefinição na tela de login.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="new-password" className="block text-xs font-medium text-text-secondary mb-1.5">
                  Nova senha
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-sm border border-border bg-bg-surface px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:outline-none focus:border-accent"
                    placeholder="mínimo 8 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted transition-colors hover:text-text-secondary"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="rounded-sm border border-status-red/25 bg-status-red/5 px-3 py-2 text-xs text-status-red">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-1 flex h-9 w-full items-center justify-center gap-2 rounded-sm bg-accent text-sm font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? 'Salvando…' : 'Salvar nova senha'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
